<template>
    <div class="computerBox live-page">
        <topBox :title="$t('page.live')" />
        <div class="live-body">
            <!-- 默认 WebRTC 低延迟；卡顿再切 FLV 稳定模式 -->
            <div class="mode-bar">
                <button
                    type="button"
                    class="mode-btn"
                    :class="{ active: mode === 'webrtc' }"
                    @click="switchMode('webrtc')"
                >{{ $t('text.liveModeLowLatency') }}</button>
                <button
                    type="button"
                    class="mode-btn"
                    :class="{ active: mode === 'flv' }"
                    @click="switchMode('flv')"
                >{{ $t('text.liveModeStable') }}</button>
            </div>

            <div class="player-wrap">
                <!-- WebRTC：同域嵌入 go2rtc 自带页（经 nginx / 本地 proxy），接近直开体验 -->
                <iframe
                    v-if="mode === 'webrtc'"
                    ref="webrtcFrame"
                    :key="webrtcKey"
                    class="player-frame"
                    :src="webrtcUrl"
                    allow="autoplay; fullscreen; microphone; camera"
                    allowfullscreen
                    referrerpolicy="no-referrer-when-downgrade"
                    @load="onWebrtcFrameLoad"
                />

                <!-- HTTP-FLV：弱网兜底（iOS Safari 基本不支持 flv.js，请用低延迟） -->
                <video
                    v-show="mode === 'flv'"
                    ref="flvVideo"
                    class="player-video"
                    controls
                    autoplay
                    muted
                    playsinline
                    webkit-playsinline
                />

                <!-- iOS / 浏览器策略：带声音不能自动播，需用户点一次 -->
                <button
                    v-if="needSoundTap"
                    type="button"
                    class="sound-unlock"
                    @click="unlockSound"
                >{{ $t('text.liveTapForSound') }}</button>

                <div v-if="tip && !needSoundTap" class="player-tip">{{ tip }}</div>
            </div>
        </div>
        <mainMenu />
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import flvjs from 'flv.js'
import topBox from '@/components/computer/TopBox.vue'
import mainMenu from '@/components/computer/MainMenu.vue'

/**
 * 直播优先 WebRTC（低延迟）。
 * 播放地址用同域相对路径：开发走 vue proxy，生产走 nginx 反代 go2rtc。
 *
 * iPhone 无声原因：Safari 禁止「未静音」自动播放，必须用户手势后才能出声。
 */
const LIVE_SRC = 'living_room'
const STORAGE_KEY = 'live_mode'

function isLikelyIos() {
    if (typeof navigator === 'undefined') return false
    const ua = navigator.userAgent || ''
    return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export default {
    components: {
        topBox,
        mainMenu,
    },
    data() {
        return {
            lang: '',
            mode: localStorage.getItem(STORAGE_KEY) === 'flv' ? 'flv' : 'webrtc',
            tip: '',
            flvPlayer: null,
            webrtcKey: 0,
            needSoundTap: true,
            webrtcUrl: `/stream.html?src=${LIVE_SRC}`,
            flvUrl: `/api/stream.flv?src=${LIVE_SRC}`,
        }
    },
    mounted() {
        const { locale } = useI18n()
        this.lang = localStorage.getItem('lang') ? localStorage.getItem('lang') : locale.value
        locale.value = this.lang
        // iOS 上 FLV/MSE 不可用，强制走 WebRTC
        if (isLikelyIos() && this.mode === 'flv') {
            this.mode = 'webrtc'
            localStorage.setItem(STORAGE_KEY, 'webrtc')
        }
        if (this.mode === 'flv') {
            this.$nextTick(() => this.startFlv())
        }
    },
    beforeUnmount() {
        this.destroyFlv()
    },
    methods: {
        switchMode(next) {
            if (next === this.mode) return
            if (isLikelyIos() && next === 'flv') {
                this.tip = this.$t('text.liveFlvUnsupported')
                return
            }
            this.mode = next
            localStorage.setItem(STORAGE_KEY, next)
            this.tip = ''
            this.needSoundTap = true
            if (next === 'flv') {
                this.$nextTick(() => this.startFlv())
            } else {
                this.destroyFlv()
                this.webrtcKey += 1
            }
        },
        onWebrtcFrameLoad() {
            // 同域时可探测到 video；是否已出声仍以用户点击为准（iOS 策略）
            this.needSoundTap = true
        },
        unlockSound() {
            // 必须在用户点击回调里 unmute / play，否则 iOS 仍会静音
            const iframe = this.$refs.webrtcFrame
            if (iframe) {
                try {
                    const doc = iframe.contentDocument || iframe.contentWindow?.document
                    if (doc) {
                        doc.querySelectorAll('video, audio').forEach((el) => {
                            el.muted = false
                            el.defaultMuted = false
                            el.volume = 1
                            const p = el.play()
                            if (p && typeof p.catch === 'function') p.catch(() => {})
                        })
                        // go2rtc 播放器上常见静音按钮
                        const unmuteBtn = doc.querySelector(
                            '[title*="nmuted"], [aria-label*="nmuted"], .vjs-mute-control, button[class*="mute"]'
                        )
                        if (unmuteBtn) unmuteBtn.click()
                    }
                } catch (e) {
                    // 跨源时无法操作内部 video，引导用户点画面内控件
                    console.warn('unlock iframe media failed', e)
                }
            }

            const video = this.$refs.flvVideo
            if (video && this.mode === 'flv') {
                video.muted = false
                video.volume = 1
                const p = video.play()
                if (p && typeof p.catch === 'function') {
                    p.catch(() => {
                        this.tip = this.$t('text.liveTapToPlay')
                    })
                }
            }

            this.needSoundTap = false
        },
        startFlv() {
            this.destroyFlv()
            const video = this.$refs.flvVideo
            if (!video) return

            if (!flvjs.isSupported()) {
                this.tip = this.$t('text.liveFlvUnsupported')
                this.needSoundTap = false
                return
            }

            try {
                this.flvPlayer = flvjs.createPlayer(
                    {
                        type: 'flv',
                        isLive: true,
                        hasAudio: true,
                        hasVideo: true,
                        url: this.flvUrl,
                    },
                    {
                        enableStashBuffer: true,
                        stashInitialSize: 128,
                        lazyLoad: false,
                        autoCleanupSourceBuffer: true,
                    }
                )
                this.flvPlayer.attachMediaElement(video)
                this.flvPlayer.load()
                // 先静音自动播（过审策略），声音靠「点击开启声音」
                video.muted = true
                this.flvPlayer.play().catch(() => {
                    this.tip = this.$t('text.liveTapToPlay')
                })
                this.flvPlayer.on(flvjs.Events.ERROR, (errType, errDetail) => {
                    console.error('FLV error', errType, errDetail)
                    this.tip = this.$t('text.liveFlvError')
                })
                this.needSoundTap = true
            } catch (e) {
                console.error(e)
                this.tip = this.$t('text.liveFlvError')
            }
        },
        destroyFlv() {
            if (!this.flvPlayer) return
            try {
                this.flvPlayer.pause()
                this.flvPlayer.unload()
                this.flvPlayer.detachMediaElement()
                this.flvPlayer.destroy()
            } catch (e) {
                // ignore teardown errors
            }
            this.flvPlayer = null
        },
    },
}
</script>

<style lang="scss" scoped>
.live-page {
    background: #0f1419;
    min-height: 100vh;
}

.live-body {
    width: 100%;
    margin-top: 80px;
    padding: 0 16px 80px;
    box-sizing: border-box;
}

.mode-bar {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 12px;
}

.mode-btn {
    padding: 12px 8px;
    border-radius: 999px;
    border: 1px solid #2a3441;
    background: #1a222c;
    color: #94a3b8;
    font-size: 13px;
    cursor: pointer;
}

.mode-btn.active {
    background: #22c55e;
    border-color: #22c55e;
    color: #fff;
    font-weight: 600;
}

.player-wrap {
    position: relative;
    width: 100%;
    height: calc(100vh - 200px);
    min-height: 280px;
    border-radius: 16px;
    overflow: hidden;
    background: #000;
}

.player-frame,
.player-video {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
    background: #000;
    object-fit: contain;
}

.sound-unlock {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 16px 24px;
    border: 0;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.player-tip {
    position: absolute;
    left: 12px;
    bottom: 12px;
    right: 12px;
    z-index: 1;
    padding: 8px 12px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.65);
    color: #e2e8f0;
    font-size: 13px;
    line-height: 1.4;
    pointer-events: none;
}
</style>
